import * as React from "react";
import { useLocation } from "react-router";

import { IngestionLabelBadge } from "@/shared/components/files/IngestionLabelBadge";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { SESSION_INGESTION_ANCHOR } from "@/shared/components/sessions";
import { FormattedDateTime } from "@/shared/datetime";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { INGEST_FILE_OUTCOME_MESSAGES } from "@/shared/domain/ingestionRules";
import { useIngestFiles } from "@/shared/domain/ingestionRules/hooks";
import { useOrgMemberDirectory } from "@/shared/domain/orgs/hooks";
import {
  type PendingIngestionFile,
  type SessionIngestionStatus,
  type SessionRecord,
  skippedFileCount,
} from "@/shared/domain/sessions";
import {
  useSessionIngestionStatus,
  useSkipWaitingFor,
} from "@/shared/domain/sessions/hooks";
import { ConfirmDialog, RobotoButton } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation";
import { Principal } from "@/shared/principal";

import styles from "./SessionDetailsTab.module.css";
import {
  ingestionRunFailed,
  PENDING_FILE_INGESTION_STATUS,
} from "./sessionIngestion";

/**
 * What "Run again" did, when it did not start a run. Worded as the file
 * label's popup words the same outcomes.
 */
/**
 * The session's files that are not ingested yet, with what can be done about
 * each, and the files it was told not to wait for. Links to
 * `#session-ingestion` scroll here.
 */
export function SessionIngestionPanel({ session }: { session: SessionRecord }) {
  const released = useCheckFeatureFlag(
    "release-session-ingestion",
    session.org_id,
  );
  const visible = released || session.status === "complete";
  const { data: status, error } = useSessionIngestionStatus({
    session,
    enabled: visible,
  });
  const ref = React.useRef<HTMLDivElement>(null);
  const showPending = (status?.pending_file_count ?? 0) > 0;
  const showSkipped = status !== undefined && skippedFileCount(status) > 0;
  const showError = status === undefined && error !== null;
  const shown = visible && (showPending || showSkipped || showError);
  useScrollIntoViewWhenLinked(ref, shown);

  if (!shown) {
    return null;
  }
  return (
    <div
      ref={ref}
      id={SESSION_INGESTION_ANCHOR}
      className={styles.ingestionPanel}
      data-testid="session-ingestion-panel"
    >
      {showError && (
        <section className={styles.card}>
          <h3 className={styles.cardHeading}>Ingestion</h3>
          <p className={styles.ingestionNote}>
            Could not load the ingestion status.
          </p>
        </section>
      )}
      {status !== undefined && showPending && (
        <PendingFiles session={session} status={status} />
      )}
      {status !== undefined && showSkipped && (
        <SkippedFiles session={session} status={status} />
      )}
    </div>
  );
}

function useScrollIntoViewWhenLinked(
  ref: React.RefObject<HTMLElement | null>,
  shown: boolean,
) {
  const { hash } = useLocation();
  React.useEffect(() => {
    if (shown && hash === `#${SESSION_INGESTION_ANCHOR}`) {
      ref.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, [ref, shown, hash]);
}

function PendingFiles({
  session,
  status,
}: {
  session: SessionRecord;
  status: SessionIngestionStatus;
}) {
  const [skipping, setSkipping] = React.useState<{
    files: PendingIngestionFile[];
    open: boolean;
  }>({ files: [], open: false });
  const failed = status.pending_files.filter(ingestionRunFailed);
  const waiting = status.state === "processing";
  const truncated = status.pending_file_count > status.pending_files.length;

  return (
    <section className={styles.card} data-testid="session-ingestion-pending">
      <h3 className={styles.cardHeading}>
        {waiting ? "Waiting on ingestion" : "Not ingested yet"}
      </h3>
      <p className={styles.ingestionNote}>
        {waiting
          ? "This session counts as fully ingested after these files are ingested."
          : "After the session is complete, it waits for these files to be ingested before it counts as fully ingested."}
        {truncated &&
          ` Showing the first ${status.pending_files.length} of ${status.pending_file_count}.`}
      </p>
      {failed.length >= 2 && (
        <div className={styles.bulkActions}>
          <RobotoButton
            variant="secondary"
            size="sm"
            onClick={() => setSkipping({ files: failed, open: true })}
          >
            {"Don't wait for failed files"}
          </RobotoButton>
        </div>
      )}
      <ul className={styles.pendingFileList}>
        {status.pending_files.map((file) => (
          <PendingFileRow
            key={file.file_id}
            session={session}
            file={file}
            onSkip={() => setSkipping({ files: [file], open: true })}
          />
        ))}
      </ul>
      <SkipFilesDialog
        session={session}
        files={skipping.files}
        open={skipping.open}
        onClose={() => setSkipping((prev) => ({ ...prev, open: false }))}
      />
    </section>
  );
}

function PendingFileRow({
  session,
  file,
  onSkip,
}: {
  session: SessionRecord;
  file: PendingIngestionFile;
  onSkip: () => void;
}) {
  const runAgain = useIngestFiles(session.org_id);
  const run = file.last_run;
  const failed = ingestionRunFailed(file);
  const outcome = runAgain.data?.[0]?.outcome;
  const outcomeNote =
    outcome === undefined || outcome === "started"
      ? undefined
      : INGEST_FILE_OUTCOME_MESSAGES[outcome];

  return (
    <li
      className={styles.pendingFileRow}
      data-testid="session-ingestion-pending-file"
    >
      <div className={styles.pendingFileMain}>
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Files}/${file.file_id}`}
          className={styles.pendingFilePath}
        >
          {file.relative_path}
        </RobotoLinkHoverUnderline>
        {file.uploaded !== null && (
          <span className={styles.pendingFileMeta}>
            Waiting since <FormattedDateTime value={file.uploaded} />
          </span>
        )}
        {failed && run?.detail && (
          <span className={styles.pendingFileMeta}>{run.detail}</span>
        )}
        {outcomeNote !== undefined && (
          <span className={styles.pendingFileMeta} role="status">
            {outcomeNote}
          </span>
        )}
        {runAgain.error && (
          <span className={styles.completeError} role="alert">
            {userFacingErrorMessage(
              runAgain.error,
              "Could not run the ingestion again.",
            )}
          </span>
        )}
      </div>
      <span className={styles.pendingFileActions}>
        <IngestionLabelBadge
          file={{
            file_id: file.file_id,
            created: file.uploaded ?? undefined,
            ingestion_status:
              PENDING_FILE_INGESTION_STATUS[file.ingestion_status],
            ingestable: true,
            relative_path: file.relative_path,
            org_id: session.org_id,
          }}
          latestRun={run}
        />
        {run?.invocation_id && (
          <RobotoLinkHoverUnderline
            to={`${Goto.route.Invocations}/${run.invocation_id}`}
            aria-label={`View invocation for ${file.relative_path}`}
          >
            View invocation
          </RobotoLinkHoverUnderline>
        )}
        {failed && (
          <RobotoButton
            variant="secondary"
            size="sm"
            loading={runAgain.isPending}
            onClick={() => runAgain.mutate([file.file_id])}
            aria-label={`Run again: ${file.relative_path}`}
          >
            Run again
          </RobotoButton>
        )}
        <RobotoButton
          variant="ghost"
          size="sm"
          onClick={onSkip}
          aria-label={`Don't wait for ${file.relative_path}`}
        >
          {"Don't wait"}
        </RobotoButton>
      </span>
    </li>
  );
}

function SkipFilesDialog({
  session,
  files,
  open,
  onClose,
}: {
  session: SessionRecord;
  files: PendingIngestionFile[];
  open: boolean;
  onClose: () => void;
}) {
  const { mutate, isPending, error, reset } = useSkipWaitingFor();
  const one = files.length === 1;
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
          onClose();
        }
      }}
      title={
        one
          ? "Stop waiting for this file?"
          : `Stop waiting for ${files.length} failed files?`
      }
      description={
        one
          ? "The file stays in the session and you can still open it. Roboto just stops waiting for it before the session counts as fully ingested."
          : "The files stay in the session and you can still open them. Roboto just stops waiting for them before the session counts as fully ingested."
      }
      confirmLabel={
        one ? "Don't wait for this file" : "Don't wait for failed files"
      }
      pending={isPending}
      onConfirm={() =>
        mutate(
          {
            sessionId: session.session_id,
            fileIds: files.map((file) => file.file_id),
          },
          { onSuccess: onClose },
        )
      }
    >
      {error && (
        <p className={styles.completeError} role="alert">
          {userFacingErrorMessage(error, "Could not stop waiting.")}
        </p>
      )}
    </ConfirmDialog>
  );
}

function SkippedFiles({
  session,
  status,
}: {
  session: SessionRecord;
  status: SessionIngestionStatus;
}) {
  const members = useOrgMemberDirectory(session.org_id);
  const files = status.skipped_files;
  const count = skippedFileCount(status);
  return (
    <section className={styles.card} data-testid="session-ingestion-skipped">
      <h3 className={styles.cardHeading}>Not waited for</h3>
      <p className={styles.ingestionNote}>
        These files are not ingested, and the session does not wait for them.
        They stay in the session. If a new version of one is uploaded, the
        session waits for it again.
        {count > files.length &&
          ` Showing the first ${files.length} of ${count}.`}
      </p>
      <ul className={styles.pendingFileList}>
        {files.map((file) => (
          <li
            key={file.file_id}
            className={styles.pendingFileRow}
            data-testid="session-ingestion-skipped-file"
          >
            <div className={styles.pendingFileMain}>
              <RobotoLinkHoverUnderline
                to={`${Goto.route.Files}/${file.file_id}`}
                className={styles.pendingFilePath}
              >
                {file.relative_path}
              </RobotoLinkHoverUnderline>
              {(file.skipped_at !== null || file.skipped_by !== null) && (
                <span className={styles.pendingFileMeta}>
                  Stopped waiting
                  {file.skipped_at !== null && (
                    <>
                      {" on "}
                      <FormattedDateTime value={file.skipped_at} />
                    </>
                  )}
                  {file.skipped_by !== null && (
                    <>
                      {" by "}
                      <PrincipalName
                        principal={file.skipped_by}
                        members={members}
                      />
                    </>
                  )}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A user by name when they're an org member; any other principal by id. */
function PrincipalName({
  principal,
  members,
}: {
  principal: string;
  members: Map<string, { name?: string }>;
}) {
  const parsed = Principal.fromString(principal).ok;
  if (parsed !== undefined && parsed.ptype !== "user") {
    return (
      <RobotoPrincipalUnderlineLink
        className={styles.inlinePrincipal}
        principal={parsed}
      />
    );
  }
  const userId = parsed?.id ?? principal;
  return (
    <RobotoLinkHoverUnderline
      to={`${Goto.route.Users}/${encodeURIComponent(userId)}`}
    >
      {members.get(userId)?.name ?? userId}
    </RobotoLinkHoverUnderline>
  );
}
